import CommentIcon from "@mui/icons-material/Comment";
import DescriptionIcon from "@mui/icons-material/Description";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { previewFile, Toolbar } from "common";
import { useFileUrl } from "common/components/Inputs/File/useDownloadUrl";
import useDictionaries from "features/dictionaries/useDictionaries";
import { ProgramVariantItem } from "features/program-variants/programVariantApi";
import { ProgramItem } from "features/programs/programApi";
import moment from "moment";
import { BookingDocumentItem } from "./bookingDocumentApi";

function FilePreviewButton({
  fileId,
  fileName,
  fileExtension,
  title,
}: {
  fileId: number;
  fileName?: string;
  fileExtension?: string;
  title?: string;
}) {
  const url = useFileUrl(fileId, true) as string;
  if (!url) return null;

  function handlePreview() {
    previewFile({
      url: url,
      title,
      fileName:
        fileName && fileExtension ? `${fileName}.${fileExtension}` : fileName,
    });
  }

  return (
    <Tooltip title="Előnézet">
      <IconButton size="small" onClick={handlePreview}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export default function ScriptListItem({
  item,
}: {
  item: BookingDocumentItem;
}) {
  const { resolveOption } = useDictionaries();

  const booking = item.booking;
  const event = booking?.event;

  const startTime = event?.start_time
    ? moment(event.start_time).format("YYYY-MM-DD HH:mm")
    : booking?.booking_number || `#${item.booking_id}`;

  const program = event?.program_id
    ? resolveOption<ProgramItem>("programs", event.program_id)
    : undefined;

  const programVariant = booking?.program_variant_id
    ? resolveOption<ProgramVariantItem>("variants", booking.program_variant_id)
    : undefined;

  const boatName = (event as any)?.boat?.name || "";

  const qty = (booking?.number_of_adults || 0) + (booking?.number_of_kids || 0);
  const qty_babies = booking?.number_of_babies || 0;
  const qty_guides = booking?.number_of_guides || 0;

  return (
    <ListItem divider alignItems="flex-start">
      <ListItemText
        disableTypography
        primary={
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              {/* Event header: start time, boat, program */}
              <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                {program && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "5px",
                      flexShrink: 0,
                      backgroundColor: program.color,
                    }}
                  />
                )}
                <Typography fontWeight="bold">{startTime}</Typography>
                {boatName && (
                  <Typography color="text.secondary">{boatName}</Typography>
                )}
                {program && (
                  <Typography color="text.secondary">{program.name}</Typography>
                )}
              </Toolbar>

              {booking && (
                <>
                  {/* Booking name + status badges */}
                  <Toolbar sx={{ gap: 1 }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      {booking.name}
                    </Typography>
                    {!!booking.check_in_at && (
                      <Box
                        sx={{
                          backgroundColor: "success.dark",
                          fontSize: "0.7rem",
                          height: "18px",
                          px: "3px",
                          borderRadius: "3px",
                          color: theme =>
                            theme.palette.getContrastText(
                              theme.palette.success.dark,
                            ),
                        }}
                      >
                        megérkezett
                      </Box>
                    )}
                    {!!booking.armband_at && (
                      <Box
                        sx={{
                          backgroundColor: "info.dark",
                          fontSize: "0.7rem",
                          height: "18px",
                          px: "3px",
                          borderRadius: "3px",
                          color: theme =>
                            theme.palette.getContrastText(
                              theme.palette.info.dark,
                            ),
                        }}
                      >
                        karszalag
                      </Box>
                    )}
                  </Toolbar>

                  {/* Email */}
                  {booking.email && (
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {booking.email}
                    </Typography>
                  )}

                  {/* Description */}
                  {booking.description && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: theme => theme.palette.info.main,
                      }}
                    >
                      <CommentIcon fontSize="small" />
                      <Typography variant="body2">
                        {booking.description}
                      </Typography>
                    </Box>
                  )}

                  {/* Program variant + guest count + tables */}
                  <Toolbar sx={{ gap: 1 }}>
                    {program && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "5px",
                          flexShrink: 0,
                          backgroundColor: program.color,
                        }}
                      />
                    )}
                    {programVariant && (
                      <Typography>{programVariant.name}</Typography>
                    )}
                    {qty > 0 && (
                      <Typography>
                        <b>{qty}</b> fő
                        {qty_babies > 0 ? ` + ${qty_babies} baba` : ""}
                        {qty_guides > 0 ? ` + ${qty_guides} kísérő` : ""}
                      </Typography>
                    )}
                    {booking.table_numbers && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <EventSeatIcon fontSize="small" />
                        <Typography variant="body1" fontWeight={500}>
                          {booking.table_numbers}
                        </Typography>
                      </Box>
                    )}
                  </Toolbar>

                  {/* Voucher, external booking number, partner name */}
                  <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                    {!!booking.voucher && (
                      <Typography
                        variant="body2"
                        sx={{
                          backgroundColor: theme =>
                            `${theme.palette.secondary.main}33`,
                          padding: "2px 4px",
                          borderRadius: "4px",
                          border: theme =>
                            `1px solid ${theme.palette.secondary.main}`,
                        }}
                      >
                        Voucher: {booking.voucher}
                      </Typography>
                    )}
                    {!!booking.external_booking_number && (
                      <Typography
                        variant="body2"
                        sx={{
                          backgroundColor: theme =>
                            `${theme.palette.primary.main}33`,
                          padding: "2px 4px",
                          borderRadius: "4px",
                          border: theme =>
                            `1px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        K. Fogl. szám.: {booking.external_booking_number}
                      </Typography>
                    )}
                    {!!booking.partner_name && (
                      <Typography variant="body2" fontWeight={700}>
                        {booking.partner_name}
                      </Typography>
                    )}
                  </Toolbar>

                  {/* Booking options */}
                  {booking.booking_options &&
                    booking.booking_options.length > 0 && (
                      <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                        {booking.booking_options.map((option, idx) => (
                          <Box key={idx} display="flex" alignItems="center">
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="text.secondary"
                            >
                              {(option as any).name || option.option?.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 700 }}
                              ml={0.5}
                            >
                              {option.qty}
                            </Typography>
                          </Box>
                        ))}
                      </Toolbar>
                    )}
                </>
              )}

              {/* Document info */}
              <Toolbar sx={{ gap: 1 }}>
                <DescriptionIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
                <Typography variant="body2">
                  {item.name || item.type}
                </Typography>
                {item.description && (
                  <Typography variant="body2" color="text.secondary">
                    — {item.description}
                  </Typography>
                )}
              </Toolbar>
            </Box>

            {/* File preview */}
            {item.file_id && (
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <FilePreviewButton
                  fileId={item.file_id}
                  fileName={item.file?.name}
                  fileExtension={item.file?.extension}
                  title={item.name}
                />
              </Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );
}
